import React from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Header } from '../../components/layout/Header';
import { fetchRepoCatalog } from '../../lib/api/forgeConfig';
import { fetchCostSummary } from '../../lib/api/epics';
import { EmptyState, PageHeader, StatusBadge } from '../../components/ui';

/**
 * /repos — landing page for the Forge Settings UI (FR-1).
 */
export const ReposListPage: React.FC = () => {
  const query = useQuery({
    queryKey: ['repos', 'catalog'],
    queryFn: fetchRepoCatalog,
  });

  const repos = query.data ?? [];

  // Per-repo cost summary in parallel — one tiny query each.
  const costQueries = useQueries({
    queries: repos.map((r) => ({
      queryKey: ['repo-cost', r.fullName],
      queryFn: () => fetchCostSummary(r.org, r.repo),
      staleTime: 60_000,
    })),
  });

  return (
    <div data-testid="repos-page" className="min-h-screen bg-slate-50">
      <Header title="Repos" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <PageHeader
          title="Repos"
          description={<>Every repo attached to a project shows up here. Open one to manage its skills and view spend.</>}
        />

        {query.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
        {query.isError && (
          <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
            {(query.error as Error).message}
          </div>
        )}
        {query.isSuccess && query.data.length === 0 && (
          <EmptyState
            testid="repos-empty"
            icon="🗂"
            title="No repos attached yet"
            description={<>Attach a repo on the <Link to="/projects" className="text-primary-500 hover:underline">projects page</Link> to see it here.</>}
          />
        )}
        {query.isSuccess && repos.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden" data-testid="repos-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Repo</th>
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Projects</th>
                  <th className="px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Cost</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {repos.map((r, i) => (
                  <tr key={r.fullName} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-mono text-xs">
                      <Link
                        to={`/repos/${encodeURIComponent(r.org)}/${encodeURIComponent(r.repo)}/skills`}
                        className="text-primary-500 hover:text-primary-600 hover:underline"
                      >
                        {r.fullName}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="flex flex-wrap gap-1">
                        {r.projectIds.map((p) => (
                          <Link
                            key={p}
                            to={`/projects/${encodeURIComponent(p)}`}
                            className="no-underline"
                          >
                            <StatusBadge tone="info">{p}</StatusBadge>
                          </Link>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <CostCell
                        loading={costQueries[i]?.isLoading ?? false}
                        data={costQueries[i]?.data}
                      />
                    </td>
                    <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                      <Link
                        to={`/repos/${encodeURIComponent(r.org)}/${encodeURIComponent(r.repo)}/skills`}
                        className="text-xs text-primary-500 hover:text-primary-600 hover:underline"
                      >
                        Skills →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

const CostCell: React.FC<{
  loading: boolean;
  data?: { epics: { count: number; costUsd: number }; bugfixes: { count: number; costUsd: number }; total: { count: number; costUsd: number } };
}> = ({ loading, data }) => {
  if (loading) {
    return <span className="inline-block h-3 w-20 bg-slate-200 rounded animate-pulse" />;
  }
  if (!data) return <span className="text-slate-400">—</span>;
  return (
    <div className="space-y-0.5 font-mono tabular-nums">
      <div className="text-slate-900 font-semibold">
        ${data.total.costUsd.toFixed(2)}
      </div>
      <div className="text-2xs text-slate-500">
        📦 {data.epics.count}{' '}
        <span className="text-slate-300">·</span>{' '}
        🔧 {data.bugfixes.count}
      </div>
    </div>
  );
};

export default ReposListPage;
